import { TandemTool } from "../../base/TandemTool.js";
import { logger } from "../../../../../../core/logger.js";

export class CreateFolderTool extends TandemTool {
  constructor() {
    super();
    this.toolName = "createFolderTool";
  }

  /**
   * @param {string|string[]} path
   * @param {boolean} [createParents=true]
   * @param {boolean} [ignoreExisting=true]
   * @returns {Promise<Object>}
   */
  async createFolder(
    path,
    createParents = true,
    ignoreExisting = true
  ) {
    try {
      const paths = Array.isArray(path) ? path : [path];
      const results = [];
      const errors = [];

      if (!paths.length) {
        return this.returnError({}, "At least one valid directory path required");
      }

      const api = this.getTandemLocalAppApi();
      if (!api?.createFolder) {
        return this.returnError(
          {},
          "Tandem folder creation requires Revolt Desktop (tandemAPI.createFolder)."
        );
      }

      const root = this.getTandemWorkingDirectoryRoot();
      if (!root) {
        return this.returnError(
          {},
          "Tandem has no project folder set (working directory path)."
        );
      }

      for (const singlePath of paths) {
        if (!singlePath || typeof singlePath !== "string") {
          errors.push({ path: singlePath, error: "Invalid directory path" });
          continue;
        }

        const normalizedPath = this.normalizeAndValidatePath(singlePath);
        if (!normalizedPath) {
          errors.push({ path: singlePath, error: "Invalid directory path" });
          continue;
        }
        const relativePath = normalizedPath.replace(/^\/+/, "") || ".";

        const res = await api.createFolder({
          rootPath: root,
          relativePath,
          createParents,
          ignoreExisting,
        });

        if (res?.success) {
          results.push({
            path: singlePath,
            action: res.action,
            success: true,
          });
        } else {
          errors.push({
            path: singlePath,
            error: res?.error || "Failed to create directory",
            success: false,
          });
        }
      }

      const totalProcessed = results.length + errors.length;
      const successful = results.length;

      if (errors.length === 0) {
        logger.log(
          { tags: "tools|tandem|createfolder|info", color1: "lime" },
          `Tandem created ${successful} folder(s)`
        );
        return this.returnSuccess(
          {
            paths: results,
            totalProcessed,
            successful,
            failed: 0,
          },
          `Successfully created ${successful} director${successful === 1 ? "y" : "ies"}`
        );
      }
      if (results.length === 0) {
        return this.returnError(
          {
            paths: errors,
            totalProcessed,
            successful: 0,
            failed: errors.length,
          },
          "Failed to create any directories"
        );
      }
      return this.returnSuccess(
        {
          paths: results,
          errors,
          totalProcessed,
          successful,
          failed: errors.length,
        },
        `Created ${successful} director${successful === 1 ? "y" : "ies"}, ${errors.length} failed`
      );
    } catch (error) {
      logger.error(
        { tags: "tools|tandem|createfolder|error", color1: "red" },
        "Tandem createFolder failed",
        error
      );
      return this.returnError({}, error.message);
    }
  }
}
